import * as React from 'react';
import { cn } from './cn';

export interface FieldProps {
  label: string;
  htmlFor: string;
  /** Required marker (gold asterisk) + aria. */
  required?: boolean;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/**
 * EDS labelled field wrapper — Design Bible §7.3.
 * Wires label ↔ control and error text ↔ control via aria-describedby.
 * Pass the matching `id` to the control and `aria-describedby={`${htmlFor}-error`}`
 * (or rely on the field id convention below).
 */
export const Field: React.FC<FieldProps> = ({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  className,
  children,
}) => {
  const errorId = `${htmlFor}-error`;
  const hintId = `${htmlFor}-hint`;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="font-eds-body text-[13px] font-semibold text-eds-fg">
        {label}
        {required && (
          <span className="ml-0.5 text-eds-gold" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && (
        <p id={hintId} className="font-eds-body text-xs text-eds-muted-fg">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="font-eds-body text-xs font-medium text-eds-danger">
          {error}
        </p>
      )}
    </div>
  );
};

export default Field;
